import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { connect } from 'react-redux';
import { FormGroup, Button, Input, FormFeedback } from 'reactstrap';
import {
  Field,
  reduxForm,
  formValueSelector,
  Form,
  change,
  arrayRemove,
} from 'redux-form';
import { toastr } from 'react-redux-toastr';
import FormItem from '../../../common/forms/FormItem';
import {
  isRequired,
  validateLength,
  validateNumber,
} from '../../../../utils/validators';
import TextInput from '../../../common/forms/input/TextInput';
import {
  clearBaggageConfiguration,
  clearCargoPriceBaggage,
  clearCreditNotes,
  getActiveSalesSession,
  getBaggageConfiguration,
  getCheckIfTicketExists,
  getCreditNotes,
  postCargoPriceBaggage,
} from '../../../../actions';
import {
  CONTINGENCY_MOTIVE_OPTION,
  DEBOUNCE_BAGGAGE_PRICE,
  DISCOUNT_CODE_TYPE,
  ITEM_CONTENT_TYPE,
  SUNAT_INVOICE_CODE,
  TRANSACTION_TYPE_PAYMENT_ON_ARRIVAL,
  EXCESS_BAGGAGE_INCREMENTAL_PRICE,
  TRANSACTION_TYPE_FREE_OF_CHARGE,
  TRANSACTION_TYPE_OTHER,
} from '../../../../config/constants';
import Alert from '../../../common/informative/Alert';
import DynamicForm from '../../../common/forms/DynamicForm';
import {
  generateBaggageCargoItemColumns,
  generateBaggageItemFormColumns,
  generateBaggageTicketFormColumns,
} from '../../../../config/dynamicFormFields';
import Loader from '../../../common/Loader';
import {
  BAGGAGE_NO_CONFIGURED_ALERT_MESSAGE,
  MODAL_ACTION_CONFIRM_MESSAGE,
  SOME_TICKET_IS_NOT_ACTIVE_ALERT_MESSAGE,
  TICKETS_IN_DIFFERENT_GROUPS_ALERT_MESSAGE,
  TICKET_WITH_DIFFERENT_ITINERARIES_ALERT_MESSAGE,
} from '../../../../config/messages';
import ConfirmationModal from '../../../common/modal/ConfirmationModal';
import FormFooter from '../../../common/forms/FormFooter';
import VoucherTypeSelect from '../../../common/forms/select/VoucherTypeSelect';
import BusinessInputGroup from '../../../common/forms/BusinessInputGroup';
import { BAGGAGE_PATH } from '../../../../config/paths';
import Select from '../../../common/forms/select/Select';
import { tzNormalizeDate } from '../../../../utils/date';
import Modal from '../../../common/modal/Modal';
import BaggageQRScannerModal from './BaggageQRScannerModal';
import { debounce } from '../../../../utils/promise';
import BaggageDiscountCodeSelect from '../../../common/forms/select/BaggageDiscountCodeSelect';
import { DEFAULT_QUERY_GET_ALL } from '../../../../config/queries';
import { getItemConditions } from '../../../../actions/baggage/ItemCondition';
import {
  buildTransactionList,
  flagUsedPinpadTransactions,
  summarizeSplit,
} from '../../../../utils/units/paymentSplit';
import MultiPaymentMethodInputGroup, {
  isPinpadLocked,
} from '../../../common/forms/MultiPaymentMethodInputGroup';

const selector = formValueSelector('BaggageForm');

const validateLength4 = validateLength(4);

const BaggageForm = ({
  initialValues,
  baggageConfiguration,
  baggageConfigurationActivity,
  activeSalesSession,
  isEdit,
  loading,
  ticketListValue,
  baggageItemListValue,
  handleSubmit,
  onSubmit,
  dispatchChange,
  dispatchGetBaggageConfiguration,
  dispatchGetActiveSalesSession,
  dispatchClearBaggageConfiguration,
  dispatchGetCheckIfTicketExists,
  dispatchArrayRemove,
  dispatchPostCargoPriceBaggage,
  mainPassengerValue,
  specialItemListValue,
  dispatchClearCargoPriceBaggage,
  loadingCargoPriceBaggage,
  dispatchGetCreditNotes,
  businessIdValue,
  dispatchClearCreditNotes,
  specialIncrementalPriceValue,
  baggageDiscountCodeValue,
  itemConditionSpecialOptions,
  itemConditionRegularOptions,
  dispatchGetItemConditions,
  excessTransactionListValue,
  pinpadLocked,
  hasPinpadPayment,
}) => {
  const {
    excessBaggageId,
    weight,
    ticketList: initialTicketList,
    baggageItemList,
  } = initialValues;

  const newTicketList = initialTicketList || [];

  const getInitialTotalWeight = () => {
    let totalWeight = 0;
    if (baggageItemList)
      baggageItemList.forEach((item) => {
        totalWeight += item.weight;
      });
    return totalWeight;
  };

  const [excessPriceBeforeDiscount, setExcessPriceBeforeDiscount] = useState(0);

  const [showWarningModal, setShowWarningModal] = useState(false);
  const [voidDescription, setVoidDescription] = useState(null);
  const [pricePerKilogram, setPricePerKilogram] = useState(0);
  const [maximumFreeWeightPerTicket, setMaximumFreeWeightPerTicket] =
    useState(0);
  const [totalWeight, setTotalWeight] = useState(getInitialTotalWeight);
  const [showBaggageExcessWeight, setShowBaggageExcessWeight] = useState(false);

  const ticketList = (ticketListValue || [])
    .map(({ ticket }) => ticket)
    .filter(Boolean);

  const disableFields = !!newTicketList.length;

  const [finalSpecialItemPrice, setFinalSpecialItemPrice] = useState(0);
  const [finalRegularItemPrice, setFinalRegularItemPrice] = useState(0);
  const [specialPriceBeforeDiscount, setSpecialPriceBeforeDiscount] =
    useState(0);

  const hasExcessToRegister =
    excessPriceBeforeDiscount > 0 || specialPriceBeforeDiscount > 0;

  const hasAmountToCharge = finalRegularItemPrice + finalSpecialItemPrice > 0;

  const [showBaggage, setShowBaggage] = useState(!!weight);
  const [disableExcessCalculateButton, setDisableExcessCalculateButton] =
    useState(true);

  const [differentGroup, setDifferentGroup] = useState(false);
  const [differentItinerary, setDifferentItinerary] = useState(false);
  const [ticketIsNotActive, setTicketIsNotActive] = useState(false);
  const [voucherCodeValue, setVoucherCodeValue] = useState(
    initialValues.voucherTypeId && initialValues.voucherTypeId.voucherCode,
  );

  const [modalBody, setModalBody] = useState(null);
  const [modalTitle, setModalTitle] = useState('');
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const [actualItinerary, setActualItinerary] = useState(null);

  const [specialDiscountCodeInput, setSpecialDiscountCodeInput] = useState('');
  const [appliedSpecialDiscountCode, setAppliedSpecialDiscountCode] =
    useState(null);
  const [appliedSpecialDiscountCodeId, setAppliedSpecialDiscountCodeId] =
    useState(null);
  const [validatingSpecialDiscount, setValidatingSpecialDiscount] =
    useState(false);

  const [pendingSpecialRecalc, setPendingSpecialRecalc] = useState(false);

  const specialItemWeightDebouncersRef = useRef({});

  useLayoutEffect(() => {
    const query = {
      ...DEFAULT_QUERY_GET_ALL,
      query: ['active:true'],
    };

    dispatchGetItemConditions(query, 'special');
    dispatchGetItemConditions(query, 'regular');
  }, []);

  const finalRegularItemPriceRef = useRef(finalRegularItemPrice);

  useEffect(() => {
    finalRegularItemPriceRef.current = finalRegularItemPrice;
  }, [finalRegularItemPrice]);

  const finalSpecialItemPriceRef = useRef(finalSpecialItemPrice);

  useEffect(() => {
    finalSpecialItemPriceRef.current = finalSpecialItemPrice;
  }, [finalSpecialItemPrice]);

  const mainPassengerValueRef = useRef(mainPassengerValue);
  const ticketListRef = useRef(ticketList);
  const specialItemListValueRef = useRef(specialItemListValue);

  // keep mounted while a pinpad charge exists: remounting resets the rows and loses the payment
  const keepPaymentMounted = hasPinpadPayment || pinpadLocked;

  const excessSplitInvalid =
    (hasAmountToCharge || hasPinpadPayment) &&
    !summarizeSplit(
      excessTransactionListValue,
      finalRegularItemPrice + finalSpecialItemPrice,
    ).isValid;

  useEffect(() => {
    mainPassengerValueRef.current = mainPassengerValue;
  }, [mainPassengerValue]);

  useEffect(() => {
    ticketListRef.current = ticketList;
  }, [ticketList]);

  useEffect(() => {
    specialItemListValueRef.current = specialItemListValue;
  }, [specialItemListValue]);

  const resetPaymentState = ({ keepPaymentMethod = false } = {}) => {
    if (!keepPaymentMethod) {
      dispatchChange('BaggageForm', 'excessPaymentMethodId', null);
    }
    dispatchChange('BaggageForm', 'creditNotes', null);
    dispatchChange('BaggageForm', 'nextPaymentMethod', null);
  };

  useEffect(() => {
    resetPaymentState();
  }, [
    excessPriceBeforeDiscount,
    specialPriceBeforeDiscount,
    finalRegularItemPrice,
    finalSpecialItemPrice,
  ]);

  useEffect(() => {
    if (!mainPassengerValue && !businessIdValue) return;

    resetPaymentState({ keepPaymentMethod: true });

    if (
      businessIdValue &&
      businessIdValue.value &&
      voucherCodeValue === SUNAT_INVOICE_CODE
    ) {
      const query = [
        `businessId:${businessIdValue.value}`,
        'isValid:true',
        'previousExcessBaggageId:!Ø',
      ];
      dispatchGetCreditNotes({ query });
      return;
    }

    if (
      mainPassengerValue &&
      mainPassengerValue.value &&
      voucherCodeValue !== SUNAT_INVOICE_CODE
    ) {
      const query = [
        `customerId:${mainPassengerValue.value}`,
        'isValid:true',
        'previousExcessBaggageId:!Ø',
      ];
      dispatchGetCreditNotes({ query });
      // return
    }
  }, [mainPassengerValue, businessIdValue, voucherCodeValue]);

  const calculateSpecialPrice = async (
    specialItemList,
    ticketId,
    discountCode = null,
    incrementalPrice = null,
  ) => {
    const weightList = specialItemList.map((item) => +item.weight || 0);

    const payload = {
      ticketId,
      weightList,
      discountCode,
      incrementalPrice,
    };

    const result = await dispatchPostCargoPriceBaggage(payload);

    if (!result || !result.items) {
      toastr.warning(
        'Alerta',
        'No se pudo calcular el precio del equipaje especial, intente nuevamente',
      );
      return null;
    }

    result.items.forEach((item, i) => {
      dispatchChange(
        'BaggageForm',
        `specialItemList[${i}].unitPrice`,
        item.unitPrice,
      );
    });

    const specialTotalWeight = result.items.reduce(
      (accumulator, item) => accumulator + Number(item.weight || 0),
      0,
    );

    const { totalPrice } = result;

    dispatchChange(
      'BaggageForm',
      'specialBaggageWeight',
      specialTotalWeight ? String(specialTotalWeight) : '',
    );

    dispatchChange('BaggageForm', 'specialBaggageExcessPrice', totalPrice);

    setFinalSpecialItemPrice(totalPrice);

    if (discountCode === null) {
      setSpecialPriceBeforeDiscount(totalPrice);
    }

    dispatchChange(
      'BaggageForm',
      'excessPrice',
      totalPrice + finalRegularItemPriceRef.current,
    );

    let finalTotalWeight = specialTotalWeight;

    baggageItemListValue.forEach((baggageItem) => {
      finalTotalWeight += +baggageItem.weight || 0;
    });

    setTotalWeight(finalTotalWeight);

    dispatchChange('BaggageForm', 'weight', finalTotalWeight);

    return result;
  };

  const clearSpecialPrices = () => {
    Object.values(specialItemWeightDebouncersRef.current).forEach((deb) => {
      if (deb && typeof deb.cancel === 'function') deb.cancel();
    });
    specialItemWeightDebouncersRef.current = {};
    setPendingSpecialRecalc(false);

    dispatchChange('BaggageForm', 'specialItemList', []);
    dispatchChange('BaggageForm', 'specialBaggageWeight', '');
    dispatchChange('BaggageForm', 'specialBaggageExcessPrice', '');
    setFinalSpecialItemPrice(0);
    setSpecialPriceBeforeDiscount(0);
    setAppliedSpecialDiscountCode(null);
    setSpecialDiscountCodeInput('');
    dispatchChange('BaggageForm', 'specialIncrementalPrice', null);
  };

  const recalcSpecialForTicket = (ticket) => {
    if (!specialItemListValue || specialItemListValue.length === 0) return;
    if (!ticket || !ticket.ticketId) return;

    setAppliedSpecialDiscountCode(null);
    setAppliedSpecialDiscountCodeId(null);
    setSpecialDiscountCodeInput('');
    dispatchChange('BaggageForm', 'specialIncrementalPrice', null);
    calculateSpecialPrice(specialItemListValue, ticket.ticketId, null);
  };

  const recalcSpecialForPassenger = (passengerId) => {
    const ticket = ticketList.find(
      ({ customerId }) => customerId === passengerId,
    );
    recalcSpecialForTicket(ticket);
  };

  const getAnchorItineraryId = () => {
    if (mainPassengerValue) {
      const mainTicket = ticketList.find(
        ({ customerId }) => customerId === mainPassengerValue.value,
      );
      if (mainTicket) return mainTicket.itineraryId;
    }
    return actualItinerary || null;
  };

  const onSpecialItemWeightChange = async (index, eventOrValue) => {
    const value =
      eventOrValue && eventOrValue.target
        ? eventOrValue.target.value
        : eventOrValue;

    setPendingSpecialRecalc(true);

    if (appliedSpecialDiscountCode) {
      setAppliedSpecialDiscountCode(null);
      setSpecialDiscountCodeInput('');
    }

    if (specialIncrementalPriceValue) {
      dispatchChange('BaggageForm', 'specialIncrementalPrice', null);
    }

    if (!specialItemWeightDebouncersRef.current[index]) {
      specialItemWeightDebouncersRef.current[index] = debounce(
        async (idx, val) => {
          try {
            const currentMainPassenger = mainPassengerValueRef.current;
            const currentTicketList = ticketListRef.current;
            const currentSpecialItemList = specialItemListValueRef.current;

            if (!currentMainPassenger || !currentMainPassenger.value) {
              setPendingSpecialRecalc(false);
              return;
            }

            if (
              !currentSpecialItemList ||
              currentSpecialItemList.length === 0
            ) {
              setPendingSpecialRecalc(false);
              return;
            }

            let newItemWeight = val;
            if (!val || Number.isNaN(Number(val))) newItemWeight = 0;
            if (Number(val) < 0) newItemWeight = 0;

            const findTicket = currentTicketList.find(
              ({ customerId }) => customerId === currentMainPassenger.value,
            );

            if (!findTicket) {
              setPendingSpecialRecalc(false);
              return;
            }

            const { ticketId } = findTicket;

            const newSpecialItemList = currentSpecialItemList.map((item, i) => {
              if (i === idx) {
                return { ...item, weight: newItemWeight };
              }
              return item;
            });

            await calculateSpecialPrice(newSpecialItemList, ticketId, null);
          } finally {
            setPendingSpecialRecalc(false);
          }
        },
        DEBOUNCE_BAGGAGE_PRICE,
      );
    }

    specialItemWeightDebouncersRef.current[index](index, value);
  };

  const totalWeightRef = useRef(totalWeight);
  const disableExcessCalculateButtonRef = useRef(disableExcessCalculateButton);

  useEffect(() => {
    totalWeightRef.current = totalWeight;
  }, [totalWeight]);

  useEffect(() => {
    disableExcessCalculateButtonRef.current = disableExcessCalculateButton;
  }, [disableExcessCalculateButton]);

  useEffect(() => {
    dispatchGetActiveSalesSession({ source: BAGGAGE_PATH });
    dispatchGetBaggageConfiguration();

    return () => {
      dispatchClearBaggageConfiguration();
      dispatchClearCargoPriceBaggage();
      dispatchClearCreditNotes();
    };
  }, []);

  useEffect(() => {
    if (
      !baggageConfiguration.isEmpty() &&
      pricePerKilogram === 0 &&
      maximumFreeWeightPerTicket === 0
    ) {
      setPricePerKilogram(baggageConfiguration.get('pricePerKilogram'));
      setMaximumFreeWeightPerTicket(
        baggageConfiguration.get('maximumFreeWeightPerTicket'),
      );
    }
  }, [baggageConfiguration]);

  useEffect(() => {
    const tickets = (ticketListValue || [])
      .map((row) => row.ticket)
      .filter(Boolean);

    const ids = tickets.map((t) => t.value);

    const seen = new Set();

    // eslint-disable-next-line
    for (let i = 0; i < ids.length; i++) {
      if (seen.has(ids[i])) {
        dispatchArrayRemove('BaggageForm', 'ticketList', i);
        toastr.warning('Alerta', 'No se pueden agregar boletos repetidos');
        return;
      }
      seen.add(ids[i]);
    }
  }, [ticketListValue]);

  useEffect(() => {
    if (!differentItinerary) return;

    const tickets = (ticketListValue || [])
      .map((row) => row.ticket)
      .filter(Boolean);

    if (tickets.length === 0) {
      setDifferentItinerary(false);
      return;
    }

    const firstItineraryId = tickets[0].itineraryId;
    const allSame = tickets.every((t) => t.itineraryId === firstItineraryId);

    if (allSame) {
      setDifferentItinerary(false);
    }
  }, [ticketListValue, differentItinerary]);

  const calculateTotalWeight = () => {
    let newTotalWeight = 0;
    baggageItemListValue.forEach((baggageItem) => {
      newTotalWeight += +baggageItem.weight || 0;
    });

    if (+newTotalWeight !== +totalWeightRef.current) {
      setTotalWeight(newTotalWeight);
      setDisableExcessCalculateButton(true);
      setShowBaggage(false);
      setDifferentGroup(false);
      setDifferentItinerary(false);
      setTicketIsNotActive(false);

      dispatchChange('BaggageForm', 'weight', newTotalWeight);
    }
  };

  const disableExcessCalculateButtonCheck = () => {
    if (
      disableExcessCalculateButtonRef.current &&
      ticketListValue.length &&
      totalWeightRef.current > 0 &&
      initialValues
    )
      setDisableExcessCalculateButton(false);
  };

  useEffect(() => {
    if (!weight) {
      calculateTotalWeight();
      disableExcessCalculateButtonCheck();
    }
  }, [weight]);

  const handleApplySpecialDiscount = async () => {
    const code = specialDiscountCodeInput.trim();

    if (!code) {
      toastr.warning('Alerta', 'Ingrese un código de descuento');
      return;
    }

    if (!mainPassengerValue) {
      toastr.warning(
        'Alerta',
        'Seleccione el pasajero principal para aplicar el código de descuento',
      );
      return;
    }

    const findTicket = ticketList.find(
      ({ customerId }) => customerId === mainPassengerValue.value,
    );

    if (!findTicket) {
      toastr.warning(
        'Alerta',
        'No se encontró el boleto para el pasajero principal',
      );
      return;
    }

    setValidatingSpecialDiscount(true);
    try {
      const currentIncrementalPrice = specialIncrementalPriceValue
        ? specialIncrementalPriceValue.value
        : null;
      const result = await calculateSpecialPrice(
        specialItemListValue,
        findTicket.ticketId,
        code,
        currentIncrementalPrice,
      );

      if (!result) return;

      const isApplied = !!result.discountCode;

      if (!isApplied) {
        if (result.feedBackMessages && result.feedBackMessages.length) {
          result.feedBackMessages.forEach((message) => {
            toastr.warning('Alerta', message);
          });
        }
        setAppliedSpecialDiscountCodeId(null);
        setAppliedSpecialDiscountCode(null);
        return;
      }

      setAppliedSpecialDiscountCodeId(result.discountCodeId);
      setAppliedSpecialDiscountCode(result.discountCode);
      toastr.success('Éxito', 'Código de descuento aplicado');
    } catch (error) {
      toastr.warning(
        'Alerta',
        (error && error.message) ||
          'No se pudo validar el código, intente nuevamente',
      );
      setAppliedSpecialDiscountCode(null);
      setAppliedSpecialDiscountCodeId(null);
    } finally {
      setValidatingSpecialDiscount(false);
    }
  };

  const handleRemoveSpecialDiscount = async () => {
    if (!mainPassengerValue) {
      toastr.warning('Alerta', 'Seleccione el pasajero principal');
      return;
    }

    const findTicket = ticketList.find(
      ({ customerId }) => customerId === mainPassengerValue.value,
    );

    if (!findTicket) {
      toastr.warning(
        'Alerta',
        'No se encontró el boleto para el pasajero principal',
      );
      return;
    }

    setValidatingSpecialDiscount(true);
    try {
      const currentIncrementalPrice = specialIncrementalPriceValue
        ? specialIncrementalPriceValue.value
        : null;
      await calculateSpecialPrice(
        specialItemListValue,
        findTicket.ticketId,
        null,
        currentIncrementalPrice,
      );
      setAppliedSpecialDiscountCode(null);
      setAppliedSpecialDiscountCodeId(null);
      setSpecialDiscountCodeInput('');
    } finally {
      setValidatingSpecialDiscount(false);
    }
  };

  const getPreviousRegularWeight = (tickets) => {
    let previousRegularWeight = 0;
    const seenBaggageIds = new Set();

    tickets.forEach((ticket) => {
      if (!ticket || !ticket.baggages) return;

      ticket.baggages.forEach((baggage) => {
        if (!baggage || baggage.status !== 'ACTIVE') return;
        if (!baggage.itemList || baggage.itemList.length === 0) return;
        if (seenBaggageIds.has(baggage.id)) return;

        seenBaggageIds.add(baggage.id);

        baggage.itemList.forEach((item) => {
          if (!item.isSpecial) {
            previousRegularWeight += +item.weight || 0;
          }
        });
      });
    });

    return previousRegularWeight;
  };

  const getAvailableFreeWeight = (ticketCount) => {
    const tickets = (ticketListValue || [])
      .map(({ ticket }) => ticket)
      .filter(Boolean);

    const previousRegularWeight = getPreviousRegularWeight(tickets);
    const freeCap = maximumFreeWeightPerTicket * ticketCount;
    return Math.max(0, freeCap - previousRegularWeight);
  };

  const calculateDiscountCode = (discountCode, price) => {
    if (!discountCode) return price;
    const { discountType } = discountCode;
    if (discountType === DISCOUNT_CODE_TYPE.PERCENTAGE.value) {
      const { maxAmount, discount } = discountCode;
      const discountAmount = Math.min((price * discount) / 100, maxAmount);
      return Math.ceil(price - discountAmount);
    }
    if (discountType === DISCOUNT_CODE_TYPE.FINAL_PRICE.value) {
      const { discount } = discountCode;
      return discount;
    }
    return price;
  };

  const getIncrementalPriceTotal = (regularList) =>
    (regularList || []).reduce(
      (accumulator, item) =>
        accumulator +
        (item && item.incrementalPrice
          ? Number(item.incrementalPrice.value) || 0
          : 0),
      0,
    );

  const computeRegularExcess = (regularList, ticketCount) => {
    const availableFreeWeight = getAvailableFreeWeight(ticketCount);

    const totalRegularWeight = (regularList || []).reduce(
      (accumulator, item) => accumulator + (+item.weight || 0),
      0,
    );

    const isExcess = totalRegularWeight > availableFreeWeight;

    const weightPrice = isExcess
      ? Math.ceil((totalRegularWeight - availableFreeWeight) * pricePerKilogram)
      : 0;

    return {
      availableFreeWeight,
      totalRegularWeight,
      isExcess,
      excessWeight: isExcess ? totalRegularWeight - availableFreeWeight : 0,
      priceBeforeDiscount: weightPrice + getIncrementalPriceTotal(regularList),
    };
  };

  const applyRegularExcess = (
    regularList,
    ticketCount,
    { discountCode = null } = {},
  ) => {
    const {
      availableFreeWeight,
      totalRegularWeight,
      isExcess,
      excessWeight,
      priceBeforeDiscount,
    } = computeRegularExcess(regularList, ticketCount);

    setShowBaggageExcessWeight(isExcess);

    dispatchChange(
      'BaggageForm',
      'baggageExcessWeight',
      isExcess ? String(excessWeight) : '',
    );

    setExcessPriceBeforeDiscount(priceBeforeDiscount);

    let finalPrice = calculateDiscountCode(discountCode, priceBeforeDiscount);
    finalPrice = finalPrice > 0 ? finalPrice : 0;

    dispatchChange(
      'BaggageForm',
      'baggageExcessPrice',
      priceBeforeDiscount > 0 ? finalPrice : '',
    );

    setFinalRegularItemPrice(finalPrice);

    dispatchChange(
      'BaggageForm',
      'excessPrice',
      finalPrice + finalSpecialItemPriceRef.current,
    );

    return { availableFreeWeight, totalRegularWeight, finalPrice };
  };

  const applyRegularRowPrices = (regularList, availableFreeWeight) => {
    (regularList || []).forEach((item, index) => {
      const itemWeightNumber = Number(item.weight || 0);
      const exceeded = Math.max(0, itemWeightNumber - availableFreeWeight);
      const price = exceeded * pricePerKilogram;
      dispatchChange(
        'BaggageForm',
        `baggageItemList[${index}].exceededWeight`,
        exceeded > 0 ? String(exceeded) : '',
      );
      dispatchChange(
        'BaggageForm',
        `baggageItemList[${index}].excessPrice`,
        exceeded > 0 ? price : '',
      );
    });
  };

  const handleExcessCalculate = (ticketListLength) => {
    dispatchChange('BaggageForm', 'baggageDiscountCode', null);

    const { availableFreeWeight } = applyRegularExcess(
      baggageItemListValue,
      ticketListLength,
    );

    applyRegularRowPrices(baggageItemListValue, availableFreeWeight);
  };

  const normalizeTickets = (list) =>
    list.flatMap((item) => {
      const ticket = item && item.ticket ? item.ticket : item;
      return ticket && Object.keys(ticket).length > 0 ? [ticket] : [];
    });

  const getFirstTicket = (currentList) => {
    const normalized = normalizeTickets(currentList);
    return normalized.length > 0 ? normalized[0] : null;
  };

  const isSameItinerary = (ticketItineraryId, referenceItineraryId) => {
    if (!referenceItineraryId) return true;
    return ticketItineraryId === referenceItineraryId;
  };

  const getTicketLabel = (ticket) => {
    if (!ticket) return '';
    const serieCorrelativo =
      ticket.label ||
      `${ticket.documentSeries || ''}-${ticket.documentCode || ''}`;
    const source = ticket.sourceLocationName || ticket.sourceLocation || '';
    const destination =
      ticket.destinationLocationName || ticket.destinationLocation || '';
    const route = source && destination ? ` (${source} → ${destination})` : '';
    return `${serieCorrelativo}${route}`;
  };

  const getAnchorTicket = () => {
    const first = getFirstTicket(ticketListValue || []);
    return first ? getTicketLabel(first) : '';
  };

  const onHandleSubmit = (formValues) => {
    if (pendingSpecialRecalc) {
      toastr.warning(
        'Alerta',
        'Espere a que termine de calcularse el precio del equipaje especial',
      );
      return;
    }

    const ticketIdList = normalizeTickets(formValues.ticketList).map(
      ({ ticketId }) => ticketId,
    );

    const {
      baggageItemList: baggageItemListValues,
      mainPassenger,
      ticketList: ticketListValues,
      specialItemList: specialItemListValues,
      baggageExcessWeight = 0,
      specialBaggageWeight = 0,
      weight: finalTotalWeight,
      excessPrice,
    } = formValues;

    const itemList = [];

    const finalBaggageItemList = baggageItemListValues || [];
    const finalSpecialItemList = specialItemListValues || [];

    const finalWeightChargeable =
      finalTotalWeight -
      (Number(baggageExcessWeight) + Number(specialBaggageWeight));

    const baggageItem = finalBaggageItemList.map((item) => ({
      itemCategoryId: item.itemCategoryId ? item.itemCategoryId.value : null,
      itemContent: item.itemContent ? item.itemContent.value : null,
      weight: item.weight ? Number(item.weight) : null,
      itemConditionIdList:
        item.itemCondition && item.itemCondition.length > 0
          ? item.itemCondition.map(({ value }) => value)
          : [],
      isSpecial: false,
      conditionDescription: item.conditionDescription || null,
      description: item.description,
    }));

    const specialItem = finalSpecialItemList.map((item) => ({
      itemCategoryId: item.itemCategoryId ? item.itemCategoryId.value : null,
      itemContent: item.itemContent ? item.itemContent.value : null,
      weight: item.weight ? Number(item.weight) : null,
      itemConditionIdList:
        item.itemCondition && item.itemCondition.length > 0
          ? item.itemCondition.map(({ value }) => value)
          : [],
      isSpecial: true,
      conditionDescription: item.conditionDescription || null,
      description: item.description,
    }));

    itemList.push(...baggageItem);
    itemList.push(...specialItem);

    const mainTicket = ticketListValues
      .flatMap(({ ticket }) => ticket)
      .find(({ customerId }) => customerId === mainPassenger.value);

    if (!mainTicket) {
      toastr.warning(
        'Alerta',
        'No se encontró el boleto para el pasajero principal',
      );
      return;
    }

    const discountCodeIdList = [];

    if (formValues.baggageDiscountCode && formValues.baggageDiscountCode.id) {
      discountCodeIdList.push(formValues.baggageDiscountCode.id);
    }

    if (appliedSpecialDiscountCode && appliedSpecialDiscountCodeId) {
      discountCodeIdList.push(appliedSpecialDiscountCodeId);
    }

    const payload = {
      ticketIdList,
      totalWeight: finalTotalWeight,
      weight: finalWeightChargeable,
      itemList,
      mainTicketId: mainTicket.ticketId,
      discountCodeIdList,
      businessId: formValues.businessId ? formValues.businessId.value : null,
    };

    if (hasExcessToRegister && hasAmountToCharge) {
      const totalPriceBeforeDiscount =
        excessPriceBeforeDiscount + specialPriceBeforeDiscount;

      const discountAmount = Math.max(
        0,
        totalPriceBeforeDiscount - Number(excessPrice),
      );

      const split = summarizeSplit(
        formValues.excessTransactionList,
        Number(excessPrice) || 0,
      );

      if (split.hasDuplicateMethods) {
        toastr.warning(
          'Alerta',
          'No puede repetir el mismo método de pago en dos filas',
        );
        return;
      }

      if (!split.isValid) {
        toastr.warning(
          'Alerta',
          'El monto asignado en los métodos de pago no coincide con el total a cobrar',
        );
        return;
      }

      const { transactions } = buildTransactionList(
        formValues.excessTransactionList,
        {
          totalPrice: Number(excessPrice) || 0,
          customerId: mainPassenger.value,
          voucherTypeId: formValues.voucherTypeId
            ? formValues.voucherTypeId.value
            : null,
          businessId: formValues.businessId
            ? formValues.businessId.value
            : null,
        },
      );

      const transactionList = transactions.map((t) => ({
        ...t,
        depositDate: t.depositDate
          ? tzNormalizeDate({ date: t.depositDate })
          : null,
      }));

      const excessBaggageRequest = {
        weight: finalTotalWeight - finalWeightChargeable,
        payerCustomerId: mainPassenger.value,
        price: Number(excessPrice) || 0,
        discountAmount,
        priceBeforeDiscount: totalPriceBeforeDiscount,
        transactionList,
        voucherTypeId: formValues.voucherTypeId
          ? formValues.voucherTypeId.value
          : null,
        contingencyDocumentSeries: formValues.contingencyDocumentSeries || null,
        contingencyDocumentCode: formValues.contingencyDocumentCode || null,
        contingencyMotive: formValues.contingencyMotive
          ? formValues.contingencyMotive.value
          : null,
        contingencyAssignmentDocumentDate:
          formValues.contingencyAssignmentDocumentDate
            ? tzNormalizeDate({
                date: formValues.contingencyAssignmentDocumentDate,
              })
            : null,
        businessId: formValues.businessId ? formValues.businessId.value : null,
        pinPadTransactions: flagUsedPinpadTransactions(
          formValues.pinPadTransactions || [],
          formValues.excessTransactionList || [],
        ),
      };

      // const paymentMethodIdList = [];

      // if (formValues.excessPaymentMethodId && hasAmountToCharge) {
      //   paymentMethodIdList.push(formValues.excessPaymentMethodId.value);
      // }

      // const isCreditNotePayment =
      //   hasAmountToCharge &&
      //   formValues.excessPaymentMethodId &&
      //   formValues.excessPaymentMethodId.transactionCode === CREDIT_NOTE;

      // if (isCreditNotePayment && formValues.nextPaymentMethod) {
      //   paymentMethodIdList.push(formValues.nextPaymentMethod.value);
      // }

      // const creditNoteIds =
      //   isCreditNotePayment &&
      //   formValues.creditNotes &&
      //   formValues.creditNotes.length > 0
      //     ? formValues.creditNotes.map(({ value }) => value)
      //     : [];

      // const excessBaggageRequest = {
      //   weight: finalTotalWeight - finalWeightChargeable,
      //   payerCustomerId: mainPassenger.value,
      //   price: Number(excessPrice) || 0,
      //   paymentMethodIdList,
      //   discountAmount,
      //   priceBeforeDiscount: totalPriceBeforeDiscount,
      //   voucherCode: formValues.voucherCode || null,
      //   voucherTypeId: formValues.voucherTypeId
      //     ? formValues.voucherTypeId.value
      //     : null,
      //   businessId: formValues.businessId ? formValues.businessId.value : null,
      //   contingencyDocumentSeries: formValues.contingencyDocumentSeries || null,
      //   contingencyDocumentCode: formValues.contingencyDocumentCode || null,
      //   creditNoteIdList: creditNoteIds,
      //   contingencyMotive: formValues.contingencyMotive
      //     ? formValues.contingencyMotive.value
      //     : null,
      //   contingencyAssignmentDocumentDate:
      //     formValues.contingencyAssignmentDocumentDate
      //       ? tzNormalizeDate({
      //           date: formValues.contingencyAssignmentDocumentDate,
      //         })
      //       : null,
      // };

      payload.excessBaggageRequest = excessBaggageRequest;
    } else if (hasPinpadPayment) {
      toastr.warning(
        'Alerta',
        'Hay un cobro POS sin exceso a cobrar. Anule el cobro antes de guardar.',
      );
      return;
    }

    onSubmit(payload);
  };

  const onHandleVoidExcessSubmit = (formValues) => {
    if (voidDescription && voidDescription.length)
      onHandleSubmit({ voidDescription, void: true, ...formValues });
  };

  const onChangeVoucherType = (option) =>
    setVoucherCodeValue(option.voucherCode);

  const handleVoidDescripcion = (e) => setVoidDescription(e.target.value);

  const showModal = () => setShowWarningModal(true);

  const closeModal = () => {
    setShowWarningModal(false);
    setVoidDescription(null);
  };

  const updateTicketList = (index, value) => {
    const otherTickets = (ticketListValue || [])
      .map((row, i) => (i !== index ? row.ticket : null))
      .filter(Boolean);

    const anchorItineraryId =
      otherTickets.length > 0 ? otherTickets[0].itineraryId : null;

    if (
      value &&
      anchorItineraryId &&
      !isSameItinerary(value.itineraryId, anchorItineraryId)
    ) {
      const anchor = getAnchorTicket();
      const rejectedLabel = getTicketLabel(value);

      toastr.warning(
        'Alerta',
        `El boleto ${rejectedLabel} no pertenece al mismo itinerario que ${anchor}. Todos los boletos deben ser del mismo itinerario.`,
        {
          timeOut: 0,
          showCloseButton: true,
          progressBar: true,
          removeOnHover: false,
        },
      );

      setTimeout(() => {
        dispatchChange('BaggageForm', `ticketList[${index}].ticket`, null);
      }, 0);
      setDifferentItinerary(true);
      return;
    }

    const currentList = [...ticketListValue];
    currentList[index] = { ticket: value };

    const tickets = currentList.map(({ ticket }) => ticket);

    const firstTicket = getFirstTicket(tickets);

    if (firstTicket) {
      const { customerId, customerFullName } = firstTicket;

      const firstCustomer = {
        value: customerId,
        label: customerFullName,
      };
      dispatchChange('BaggageForm', 'mainPassenger', firstCustomer);
      setActualItinerary(firstTicket.itineraryId);
      recalcSpecialForTicket(firstTicket);
    } else {
      dispatchChange('BaggageForm', 'mainPassenger', null);
      setActualItinerary(null);
      dispatchChange('BaggageForm', 'baggageExcessWeight', '');
      dispatchChange('BaggageForm', 'baggageExcessPrice', '');
      dispatchChange('BaggageForm', 'baggageDiscountCode', null);
      clearSpecialPrices();
      dispatchChange('BaggageForm', 'excessPrice', '');
      setExcessPriceBeforeDiscount(0);
      setFinalRegularItemPrice(0);
      setShowBaggageExcessWeight(false);
    }

    setShowBaggage(false);
    setDifferentGroup(false);
    setTicketIsNotActive(false);
    setDisableExcessCalculateButton(true);
  };

  const onWeightChange = (index, eventOrValue) => {
    let value =
      eventOrValue && eventOrValue.target
        ? eventOrValue.target.value
        : eventOrValue;

    dispatchChange('BaggageForm', 'baggageDiscountCode', null);

    if (!value || Number.isNaN(Number(value))) value = 0;

    const newBaggageItemListValue = baggageItemListValue.map((item, i) => {
      if (i === index) return { ...item, weight: value };
      return item;
    });

    const { availableFreeWeight, totalRegularWeight } = applyRegularExcess(
      newBaggageItemListValue,
      ticketListValue.length,
    );

    applyRegularRowPrices(newBaggageItemListValue, availableFreeWeight);

    let finalTotalWeight = totalRegularWeight;

    specialItemListValue.forEach((item) => {
      finalTotalWeight += +item.weight || 0;
    });

    setTotalWeight(finalTotalWeight);

    dispatchChange('BaggageForm', 'weight', finalTotalWeight);
  };

  const renderBaggageNoConfiguredAlert = () => {
    let alert = null;
    if (!baggageConfigurationActivity && baggageConfiguration.isEmpty())
      alert = <Alert message={BAGGAGE_NO_CONFIGURED_ALERT_MESSAGE} />;
    return alert;
  };

  const renderDifferentItineraryAlert = () =>
    differentItinerary ? (
      <Alert message={TICKET_WITH_DIFFERENT_ITINERARIES_ALERT_MESSAGE} />
    ) : null;

  const renderTicketIsNotActiveAlert = () =>
    ticketIsNotActive ? (
      <Alert message={SOME_TICKET_IS_NOT_ACTIVE_ALERT_MESSAGE} />
    ) : null;

  const renderDifferentGroupAlert = () =>
    differentGroup ? (
      <Alert message={TICKETS_IN_DIFFERENT_GROUPS_ALERT_MESSAGE} />
    ) : null;

  const renderPreviousWeightAlert = () => {
    const tickets = (ticketListValue || [])
      .map(({ ticket }) => ticket)
      .filter(Boolean);

    if (tickets.length === 0) return null;

    const freeCap = maximumFreeWeightPerTicket * tickets.length;
    const previousRegularWeight = getPreviousRegularWeight(tickets);
    const availableFreeWeight = getAvailableFreeWeight(tickets.length);

    const currentRegularWeight = (baggageItemListValue || []).reduce(
      (acc, item) => acc + (+item.weight || 0),
      0,
    );

    const remainingBeforeExcess = availableFreeWeight - currentRegularWeight;
    const isInExcess = remainingBeforeExcess < 0;

    const summary = (
      <div>
        <div>
          <strong>Información de equipaje regular</strong>
        </div>
        <div>
          Peso libre total ({tickets.length}{' '}
          {tickets.length === 1 ? 'boleto' : 'boletos'} ×{' '}
          {maximumFreeWeightPerTicket} kg): <strong>{freeCap} kg</strong>
        </div>
        {previousRegularWeight > 0 && (
          <div>
            Peso ya consumido en equipajes previos:{' '}
            <strong>{previousRegularWeight} kg</strong>
          </div>
        )}
        <div>
          Peso libre disponible para este registro:{' '}
          <strong>{availableFreeWeight} kg</strong>
        </div>
        <div>
          Peso actual en este registro:{' '}
          <strong>{currentRegularWeight} kg</strong>
        </div>
        {isInExcess ? (
          <div style={{ color: '#c53030' }}>
            Exceso actual: <strong>{Math.abs(remainingBeforeExcess)} kg</strong>
          </div>
        ) : (
          <div>
            Peso libre restante antes de exceso:{' '}
            <strong>{remainingBeforeExcess} kg</strong>
          </div>
        )}
      </div>
    );

    return <Alert type="info" message={summary} />;
  };

  const renderBaggage = () => {
    if (!differentGroup && !ticketIsNotActive && !differentItinerary) {
      return (
        <div className="mb-3">
          <h5>Datos de Equipaje</h5>
          <FormGroup row>
            <FormItem label="Peso Total" required>
              <Field
                name="weight"
                type="text"
                component={TextInput}
                disabled
                placeholder="Peso"
                validate={[isRequired]}
              />
            </FormItem>
          </FormGroup>
        </div>
      );
    }
    return null;
  };

  const renderContingencyFields = () => {
    let isContingencySalesSession = false;

    if (!activeSalesSession.isEmpty()) {
      isContingencySalesSession =
        activeSalesSession.get('workstation').contingency;
    }

    return isContingencySalesSession && !isEdit ? (
      <>
        <h5>Datos de Contingencia</h5>
        <FormGroup row>
          <FormItem label="Serie de Contingencia">
            <Field
              name="contingencyDocumentSeries"
              component={TextInput}
              type="text"
              placeholder="Serie de Contingencia"
              validate={
                isContingencySalesSession
                  ? [isRequired, validateNumber, validateLength4]
                  : []
              }
            />
          </FormItem>
        </FormGroup>
        <FormGroup row>
          <FormItem label="Código de Contingencia">
            <Field
              name="contingencyDocumentCode"
              component={TextInput}
              type="text"
              placeholder="Código de Contingencia"
              validate={
                isContingencySalesSession ? [isRequired, validateNumber] : []
              }
            />
          </FormItem>
        </FormGroup>
        <FormGroup row>
          <FormItem label="Motivo de Contingencia">
            <Field
              name="contingencyMotive"
              component={Select}
              placeholder="Motivo de Contingencia"
              options={CONTINGENCY_MOTIVE_OPTION}
              validate={isContingencySalesSession ? [isRequired] : []}
            />
          </FormItem>
        </FormGroup>
        <FormGroup row>
          <FormItem label="Fecha de Venta" required>
            <Field
              name="contingencyAssignmentDocumentDate"
              component={TextInput}
              type="datetime-local"
              placeholder="Fecha de Venta"
              validate={isContingencySalesSession ? [isRequired] : []}
            />
          </FormItem>
        </FormGroup>
      </>
    ) : null;
  };

  const renderExcess = () => {
    if (
      !keepPaymentMounted &&
      (differentGroup ||
        ticketIsNotActive ||
        differentItinerary ||
        !hasExcessToRegister)
    ) {
      return null;
    }

    const voucherTypeField = voucherCodeValue === SUNAT_INVOICE_CODE && (
      <BusinessInputGroup
        label="Empresa"
        labelRequired
        name="businessId"
        form="BaggageForm"
        validate={[isRequired]}
        disabled={disableFields}
        showDetails={!disableFields}
      />
    );

    return (
      <>
        <FormGroup row className="align-items-center">
          <FormItem label="Precio Final" required>
            <Field
              name="excessPrice"
              component={TextInput}
              type="text"
              placeholder="Precio Final"
              disabled
              size="lg"
            />
          </FormItem>
        </FormGroup>

        {(hasAmountToCharge || keepPaymentMounted) && (
          <>
            <FormGroup row>
              <FormItem label="Tipo de Comprobante" required>
                <Field
                  name="voucherTypeId"
                  component={VoucherTypeSelect}
                  onChange={onChangeVoucherType}
                  isClearable={false}
                  validate={[isRequired]}
                  isDisabled={disableFields}
                  displayInPaymentPage
                />
              </FormItem>
            </FormGroup>
            {voucherTypeField}
            <FormGroup row>
              <FormItem label="Método de Pago" required>
                <MultiPaymentMethodInputGroup
                  name="excessTransactionList"
                  formName="BaggageForm"
                  totalPrice={finalRegularItemPrice + finalSpecialItemPrice}
                  transactionCodesNotIncluded={[
                    TRANSACTION_TYPE_PAYMENT_ON_ARRIVAL,
                    TRANSACTION_TYPE_FREE_OF_CHARGE,
                    TRANSACTION_TYPE_OTHER,
                  ]}
                />
              </FormItem>
            </FormGroup>
            {renderContingencyFields()}
          </>
        )}
      </>
    );
  };

  const renderExcessVoidButton = () =>
    excessBaggageId ? (
      <Button type="button" onClick={showModal} color="danger" outline>
        Anular exceso
      </Button>
    ) : null;

  const renderButtons = () =>
    showBaggage &&
    !differentGroup &&
    !ticketIsNotActive &&
    !differentItinerary ? (
      <FormFooter saveButtonDisabled={!showBaggage || pinpadLocked}>
        {renderExcessVoidButton()}
      </FormFooter>
    ) : null;

  const renderModal = () => {
    const validJustification = !(voidDescription && voidDescription.length);

    const confirmModalBody = (
      <>
        <p>{MODAL_ACTION_CONFIRM_MESSAGE}</p>
        <Input
          type="textarea"
          invalid={validJustification}
          name="voidDescription"
          placeholder="Ingrese un motivo"
          onChange={handleVoidDescripcion}
        />
        <FormFeedback>Debe ingresar el motivo de la acción.</FormFeedback>
      </>
    );

    return (
      <ConfirmationModal
        show={showWarningModal}
        body={confirmModalBody}
        onClickConfirm={handleSubmit(onHandleVoidExcessSubmit)}
        onClickCancel={closeModal}
      />
    );
  };

  const handleOpenModal = () => setModalIsOpen(true);

  const handleCloseModal = () => setModalIsOpen(false);

  const handleQrConfirm = (validTickets) => {
    const currentList = ticketListValue || [];
    const existingIds = currentList
      .map((row) => row.ticket && row.ticket.value)
      .filter(Boolean);

    const currentItinerary =
      actualItinerary ||
      (validTickets.length > 0 ? validTickets[0].itineraryId : null);

    const filteredByItinerary = [];
    const rejectedTickets = [];

    validTickets.forEach((ticket) => {
      if (ticket.itineraryId === currentItinerary) {
        filteredByItinerary.push(ticket);
      } else {
        rejectedTickets.push(ticket);
      }
    });

    if (rejectedTickets.length > 0) {
      const anchorLabel = actualItinerary
        ? getAnchorTicket()
        : getTicketLabel(validTickets[0]);

      rejectedTickets.forEach((ticket) => {
        const rejectedLabel = getTicketLabel(ticket);
        toastr.warning(
          'Alerta',
          `El boleto ${rejectedLabel} no pertenece al mismo itinerario que ${anchorLabel}. Todos los boletos deben ser del mismo itinerario.`,
          { timeOut: 0 },
        );
      });
    }

    const newRows = filteredByItinerary
      .filter((ticket) => !existingIds.includes(ticket.ticketId))
      .map((ticket) => ({
        ticket: {
          value: ticket.ticketId,
          label: `${ticket.documentSeries}-${ticket.documentCode}`,
          customerFullName: ticket.customerFullName,
          customerDocumentNumber: ticket.documentNumber,
          sourceLocationName: ticket.sourceLocation,
          destinationLocationName: ticket.destinationLocation,
          departureDate: ticket.departureDate,
          customerId: ticket.customerId,
          baggages: ticket.baggages,
          itineraryId: ticket.itineraryId,
          ticketStatus: ticket.ticketStatus,
          ticketId: ticket.ticketId,
        },
      }));

    if (!newRows.length) {
      handleCloseModal();
      return;
    }

    const updatedList = [...currentList, ...newRows];

    dispatchChange('BaggageForm', 'ticketList', updatedList);

    if (newRows.length > 0) {
      toastr.success(
        'Boletos agregados',
        `Se agregaron ${newRows.length} boleto(s) al formulario`,
      );
    }

    if (updatedList && updatedList.length) {
      const firstValidTicket = getFirstTicket(updatedList);
      dispatchChange('BaggageForm', 'mainPassenger', {
        value: firstValidTicket.customerId,
        label: firstValidTicket.customerFullName,
      });
      setActualItinerary(firstValidTicket.itineraryId);
      recalcSpecialForTicket(firstValidTicket);
    } else {
      dispatchChange('BaggageForm', 'mainPassenger', null);
      setActualItinerary(null);
    }

    handleCloseModal();

    handleExcessCalculate(updatedList.length);
  };

  const handleQrCheck = async (code) => {
    const codeParts = code.split('-');

    if (codeParts.length < 2) {
      return Promise.reject(new Error('Código de boleto inválido'));
    }

    const documentCode = codeParts[codeParts.length - 1];
    const documentSeries = codeParts.slice(0, -1).join('-');

    const newDocumentSeries = documentSeries.substring(1);

    const result = await dispatchGetCheckIfTicketExists({
      documentSeries: newDocumentSeries,
      documentCode,
    });

    if (!result || !result.exists) {
      return Promise.reject(new Error('Boleto no encontrado'));
    }

    return result;
  };

  const handleRemoveTicket = (index, fields) => {
    const newList = [...ticketListValue];
    newList.splice(index, 1);
    const firstTicket = getFirstTicket(newList);
    fields.remove(index);
    if (!firstTicket) {
      dispatchChange('BaggageForm', 'mainPassenger', null);
      dispatchChange('BaggageForm', 'baggageExcessWeight', '');
      setActualItinerary(null);
      setDifferentItinerary(false);
      dispatchChange('BaggageForm', 'baggageExcessPrice', '');
      dispatchChange('BaggageForm', 'baggageDiscountCode', null);
      setExcessPriceBeforeDiscount(0);
      setFinalRegularItemPrice(0);
      setShowBaggageExcessWeight(false);
      clearSpecialPrices();
      dispatchChange('BaggageForm', 'excessPrice', '');
      return;
    }

    dispatchChange('BaggageForm', 'mainPassenger', {
      value: firstTicket.customerId,
      label: firstTicket.customerFullName,
    });
    setActualItinerary(firstTicket.itineraryId);
    setDifferentItinerary(false);

    recalcSpecialForTicket(firstTicket);

    if (baggageItemListValue.length) {
      handleExcessCalculate(newList.length);
    }
  };

  const handleAddTicket = (fields) => {
    fields.push({});

    const newList = [...ticketListValue, {}];

    if (baggageItemListValue.length === 0) {
      dispatchChange('BaggageForm', 'excessPrice', finalSpecialItemPrice);
      return;
    }

    handleExcessCalculate(newList.length);
  };

  const handleBulkScan = () => {
    handleOpenModal();
    setModalTitle('Escaneo de múltiples boletos');
    setModalBody(
      <BaggageQRScannerModal
        onConfirm={handleQrConfirm}
        onCancel={handleCloseModal}
        validateTicket={handleQrCheck}
        defaultItineraryId={getAnchorItineraryId()}
      />,
    );
  };

  const ticketGenerateExtraButtons = () => {
    if (isEdit) return null;
    return [
      {
        label: 'Agregar por código QR',
        color: 'success',
        icon: 'fa fa-qrcode',
        onClick: handleBulkScan,
      },
    ];
  };

  const recalculateRegularExcess = (rawList) => {
    const { availableFreeWeight, totalRegularWeight } = computeRegularExcess(
      rawList,
      ticketListValue.length,
    );

    const computedList = rawList.map((item) => {
      const itemWeightNumber = +item.weight || 0;

      const exceeded = Math.max(0, itemWeightNumber - availableFreeWeight);
      const price = exceeded * pricePerKilogram;

      return {
        ...item,
        exceededWeight: exceeded > 0 ? String(exceeded) : '',
        excessPrice: exceeded > 0 ? price : '',
      };
    });

    dispatchChange('BaggageForm', 'baggageItemList', computedList);
    dispatchChange('BaggageForm', 'baggageDiscountCode', null);

    applyRegularExcess(rawList, ticketListValue.length);

    let finalTotalWeight = totalRegularWeight;
    specialItemListValue.forEach((item) => {
      finalTotalWeight += +item.weight || 0;
    });
    setTotalWeight(finalTotalWeight);
    dispatchChange('BaggageForm', 'weight', finalTotalWeight);
  };

  const handleDuplicateRegularItem = (index) => {
    const source = baggageItemListValue[index];
    if (!source) return;
    const clone = {
      itemCategoryId: source.itemCategoryId,
      itemContent: source.itemContent,
      itemCondition: source.itemCondition
        ? [...source.itemCondition]
        : source.itemCondition,
      conditionDescription: source.conditionDescription,
      description: source.description,
      weight: source.weight,
      incrementalPrice: source.incrementalPrice,
    };

    recalculateRegularExcess([...baggageItemListValue, clone]);
  };

  const regularItemButtonsPerRow = [
    {
      label: 'Duplicar',
      color: 'primary',
      icon: 'fa fa-clone',
      onClick: handleDuplicateRegularItem,
    },
  ];

  const onChangeMainPassenger = (option) => {
    if (!option) {
      setActualItinerary(null);
      clearSpecialPrices();
      dispatchChange(
        'BaggageForm',
        'excessPrice',
        finalRegularItemPriceRef.current,
      );
      return;
    }

    const ticket = ticketList.find(
      ({ customerId }) => customerId === option.value,
    );
    if (!ticket) return;

    setActualItinerary(ticket.itineraryId);
    recalcSpecialForPassenger(option.value);
  };

  const handleAddBaggageItem = (fields) => {
    dispatchChange('BaggageForm', 'baggageDiscountCode', null);

    applyRegularExcess(baggageItemListValue, ticketListValue.length);

    const defaults = Object.values(ITEM_CONTENT_TYPE).filter(
      (itemContentType) => itemContentType.default,
    );
    const defaultItemContent = defaults[0] || ITEM_CONTENT_TYPE.NO_DECLARATION;

    const itemConditionDefaults = itemConditionRegularOptions
      .filter((option) => option.isDefault)
      .map((option) => ({ ...option, value: option.id, label: option.name }));

    fields.push({
      itemContent: defaultItemContent,
      itemCondition: itemConditionDefaults,
    });
  };

  const handleAddBaggageSpecialItem = (fields) => {
    const defaults = Object.values(ITEM_CONTENT_TYPE).filter(
      (itemContentType) => itemContentType.default,
    );
    const defaultItemContent = defaults[0] || ITEM_CONTENT_TYPE.NO_DECLARATION;

    const itemConditionDefaults = itemConditionSpecialOptions
      .filter((option) => option.isDefault)
      .map((option) => ({ ...option, value: option.id, label: option.name }));

    fields.push({
      itemContent: defaultItemContent,
      itemCondition: itemConditionDefaults,
    });
  };

  const onChangeDiscountCode = (discountCode) => {
    applyRegularExcess(baggageItemListValue, ticketListValue.length, {
      discountCode,
    });
  };

  const onIncrementalPriceChange = (index, option) => {
    const newBaggageItemListValue = baggageItemListValue.map((item, i) => {
      if (i === index) return { ...item, incrementalPrice: option || null };
      return item;
    });

    applyRegularExcess(newBaggageItemListValue, ticketListValue.length, {
      discountCode: baggageDiscountCodeValue,
    });
  };

  const handleRemoveBaggageItem = (index, fields) => {
    const newList = [...baggageItemListValue];
    newList.splice(index, 1);
    fields.remove(index);

    dispatchChange('BaggageForm', 'baggageDiscountCode', null);

    const { totalRegularWeight } = applyRegularExcess(
      newList,
      ticketListValue.length,
    );

    let newFinalWeight = totalRegularWeight;

    specialItemListValue.forEach((item) => {
      newFinalWeight += +item.weight || 0;
    });

    setTotalWeight(newFinalWeight);

    dispatchChange('BaggageForm', 'weight', newFinalWeight);
  };

  const handleRemoveSpecialItem = async (index, fields) => {
    const newList = [...specialItemListValue];
    newList.splice(index, 1);
    fields.remove(index);

    if (newList.length === 0) {
      dispatchChange('BaggageForm', 'specialBaggageWeight', '');
      dispatchChange('BaggageForm', 'specialBaggageExcessPrice', '');
      setFinalSpecialItemPrice(0);
      setSpecialPriceBeforeDiscount(0);
      setAppliedSpecialDiscountCode(null);
      setSpecialDiscountCodeInput('');
      dispatchChange('BaggageForm', 'specialIncrementalPrice', null);
      dispatchChange('BaggageForm', 'excessPrice', finalRegularItemPrice);
      const newTotalWeight = baggageItemListValue.reduce(
        (accumulator, item) => accumulator + (+item.weight || 0),
        0,
      );

      setTotalWeight(newTotalWeight);
      dispatchChange('BaggageForm', 'weight', newTotalWeight);
      return;
    }

    if (!mainPassengerValue) {
      toastr.warning(
        'Alerta',
        'No se encontró el pasajero principal para calcular el precio del exceso de equipaje especial',
      );
      return;
    }

    const findTicket = ticketList.find(
      ({ customerId }) => customerId === mainPassengerValue.value,
    );

    if (!findTicket) {
      toastr.warning(
        'Alerta',
        'No se encontró el boleto para el pasajero principal',
      );
      return;
    }

    const { ticketId } = findTicket;

    setAppliedSpecialDiscountCode(null);
    setAppliedSpecialDiscountCodeId(null);
    setSpecialDiscountCodeInput('');
    dispatchChange('BaggageForm', 'specialIncrementalPrice', null);

    await calculateSpecialPrice(newList, ticketId, null);
  };

  const handleChangeSpecialIncrementalPrice = async (option) => {
    if (!mainPassengerValue) {
      toastr.warning(
        'Alerta',
        'Seleccione el pasajero principal para aplicar un incremento',
      );
      return;
    }

    const findTicket = ticketList.find(
      ({ customerId }) => customerId === mainPassengerValue.value,
    );

    if (!findTicket) {
      toastr.warning(
        'Alerta',
        'No se encontró el boleto para el pasajero principal',
      );
      return;
    }

    const incrementalPrice = option ? option.value : null;

    const currentDiscountCode = appliedSpecialDiscountCode || null;

    try {
      const result = await calculateSpecialPrice(
        specialItemListValue,
        findTicket.ticketId,
        currentDiscountCode,
        incrementalPrice,
      );

      if (!result) return;
    } catch (error) {
      toastr.warning(
        'Alerta',
        (error && error.message) ||
          'No se pudo aplicar el incremento, intente nuevamente',
      );
    }
  };

  const generateMainPassengerOptions = () =>
    ticketList.map(
      ({ customerFullName, customerId, customerDocumentNumber }) => ({
        value: customerId,
        label: `${customerDocumentNumber} - ${customerFullName}`,
      }),
    );

  const renderBaggageExcessWeight = () => {
    if (!baggageItemListValue || baggageItemListValue.length === 0) return null;

    if (!showBaggageExcessWeight && excessPriceBeforeDiscount === 0)
      return null;

    return (
      <>
        {showBaggageExcessWeight && (
          <FormGroup row>
            <FormItem label="Exceso de Equipaje" required>
              <Field
                name="baggageExcessWeight"
                type="text"
                component={TextInput}
                disabled
                validate={[isRequired]}
              />
            </FormItem>
          </FormGroup>
        )}
        <FormGroup row>
          <FormItem label="Descuento">
            <Field
              name="baggageDiscountCode"
              component={BaggageDiscountCodeSelect}
              placeholder="Codigo de descuento de Equipaje"
              onValueChange={onChangeDiscountCode}
              isDisabled={disableFields || loadingCargoPriceBaggage}
              isClearable
            />
          </FormItem>
        </FormGroup>
        <FormGroup row>
          <FormItem label="Precio" required>
            <Field
              name="baggageExcessPrice"
              type="text"
              component={TextInput}
              disabled
              validate={[isRequired]}
            />
          </FormItem>
        </FormGroup>
      </>
    );
  };

  const renderSpecialBaggageFields = () =>
    specialItemListValue && specialItemListValue.length > 0 ? (
      <>
        <FormGroup row>
          <FormItem label="Peso de Equipaje Especial" required>
            <Field
              name="specialBaggageWeight"
              component={TextInput}
              disabled
              placeholder="0"
              type="text"
              validate={[isRequired]}
            />
          </FormItem>
        </FormGroup>
        <FormGroup row>
          <FormItem label="IP">
            <Field
              name="specialIncrementalPrice"
              component={Select}
              placeholder="Seleccione un incremento"
              options={EXCESS_BAGGAGE_INCREMENTAL_PRICE}
              onValueChange={handleChangeSpecialIncrementalPrice}
              isClearable
              disabled={disableFields || loadingCargoPriceBaggage}
            />
          </FormItem>
        </FormGroup>
        <FormGroup row>
          <FormItem label="Código de Descuento">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Input
                type="text"
                placeholder="Digite el código"
                value={specialDiscountCodeInput}
                onChange={(e) => setSpecialDiscountCodeInput(e.target.value)}
                disabled={
                  disableFields ||
                  loadingCargoPriceBaggage ||
                  validatingSpecialDiscount ||
                  !!appliedSpecialDiscountCode
                }
              />
              {!appliedSpecialDiscountCode ? (
                <Button
                  type="button"
                  color="primary"
                  outline
                  onClick={handleApplySpecialDiscount}
                  disabled={
                    disableFields ||
                    loadingCargoPriceBaggage ||
                    validatingSpecialDiscount ||
                    !specialDiscountCodeInput.trim()
                  }
                >
                  {validatingSpecialDiscount ? (
                    <i className="fa fa-spinner fa-spin" />
                  ) : (
                    'Aplicar'
                  )}
                </Button>
              ) : (
                <Button
                  type="button"
                  color="danger"
                  outline
                  onClick={handleRemoveSpecialDiscount}
                  disabled={
                    disableFields ||
                    loadingCargoPriceBaggage ||
                    validatingSpecialDiscount
                  }
                >
                  Quitar
                </Button>
              )}
            </div>
            {appliedSpecialDiscountCode && (
              <small className="text-success">
                Código aplicado: <strong>{appliedSpecialDiscountCode}</strong>
              </small>
            )}
          </FormItem>
        </FormGroup>
        <FormGroup row>
          <FormItem label="Precio" required>
            <Field
              name="specialBaggageExcessPrice"
              component={TextInput}
              type="text"
              disabled
              placeholder="0"
              validate={[isRequired]}
            />
          </FormItem>
        </FormGroup>
      </>
    ) : null;

  return (
    <div>
      {loading && <Loader />}
      <Modal
        show={modalIsOpen}
        body={modalBody}
        title={modalTitle}
        onClickCancel={handleCloseModal}
        onClickClose={handleCloseModal}
      />
      {renderModal()}
      {renderBaggageNoConfiguredAlert()}
      <Form onSubmit={handleSubmit(onHandleSubmit)}>
        <div className="mb-3">
          <h5>Boletos</h5>
          <DynamicForm
            name="ticketList"
            columns={generateBaggageTicketFormColumns(
              updateTicketList,
              disableFields,
            )}
            showRemoveButton={!disableFields}
            showAddButton={!disableFields}
            extraActions={ticketGenerateExtraButtons()}
            onClickRemove={handleRemoveTicket}
            onClickAdd={handleAddTicket}
          />
          <FormGroup row>
            <FormItem label="Pasajero Principal" required>
              <Field
                name="mainPassenger"
                component={Select}
                validate={[isRequired]}
                options={generateMainPassengerOptions()}
                disabled={!ticketList.length || disableFields || isEdit}
                onValueChange={onChangeMainPassenger}
              />
            </FormItem>
          </FormGroup>
        </div>
        {renderDifferentItineraryAlert()}
        {renderTicketIsNotActiveAlert()}
        {renderDifferentGroupAlert()}
        {renderPreviousWeightAlert()}
        <div className="mb-3">
          <h5>Equipajes</h5>
          <DynamicForm
            name="baggageItemList"
            columns={generateBaggageItemFormColumns(
              disableFields || loadingCargoPriceBaggage,
              onWeightChange,
              onIncrementalPriceChange,
            )}
            showRemoveButton={!disableFields}
            showAddButton={!disableFields && mainPassengerValue}
            labelWithoutElements={
              mainPassengerValue
                ? 'No hay elementos'
                : 'Seleccione un pasajero principal para agregar equipaje'
            }
            onClickAdd={handleAddBaggageItem}
            onClickRemove={handleRemoveBaggageItem}
            otherButtonsPerRow={!disableFields ? regularItemButtonsPerRow : []}
          />
          {renderBaggageExcessWeight()}
        </div>
        <hr />
        <div className="mb-3">
          <h5>Equipaje Especial</h5>
          <DynamicForm
            name="specialItemList"
            columns={generateBaggageCargoItemColumns(
              disableFields || loadingCargoPriceBaggage,
              onSpecialItemWeightChange,
            )}
            showRemoveButton={!disableFields}
            showAddButton={!disableFields && mainPassengerValue}
            labelWithoutElements={
              mainPassengerValue
                ? 'No hay elementos'
                : 'Seleccione un pasajero principal para agregar equipaje'
            }
            onClickRemove={handleRemoveSpecialItem}
            onClickAdd={handleAddBaggageSpecialItem}
          />
          {renderSpecialBaggageFields()}
        </div>
        <hr />
        {renderBaggage()}
        {renderExcess()}
        {renderButtons()}

        <FormFooter
          saveButtonIcon={loading ? 'fa fa-spinner fa-spin' : 'fa fa-save'}
          saveButtonDisabled={
            (!baggageItemListValue.length && !specialItemListValue.length) ||
            !mainPassengerValue ||
            baggageConfiguration.isEmpty() ||
            differentItinerary ||
            loadingCargoPriceBaggage ||
            pendingSpecialRecalc ||
            validatingSpecialDiscount ||
            excessSplitInvalid ||
            loading ||
            pinpadLocked
          }
        />
      </Form>
    </div>
  );
};

BaggageForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  dispatchChange: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({
    weight: PropTypes.number,
    comment: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    tickets: PropTypes.arrayOf(PropTypes.object),
    voucherCode: PropTypes.string,
    excessBaggageId: PropTypes.number,
    ticketList: PropTypes.arrayOf(
      PropTypes.shape({
        documentCode: PropTypes.string,
      }),
    ),
    baggageItemList: PropTypes.arrayOf(
      PropTypes.shape({
        weight: PropTypes.number,
      }),
    ),
    voucherTypeId: PropTypes.shape({
      voucherCode: PropTypes.string.isRequired,
    }),
  }),
  dispatchGetBaggageConfiguration: PropTypes.func.isRequired,
  dispatchClearBaggageConfiguration: PropTypes.func.isRequired,
  baggageConfiguration: PropTypes.instanceOf(Immutable.Map).isRequired,
  baggageConfigurationActivity: PropTypes.bool.isRequired,
  baggageItemListValue: PropTypes.arrayOf(
    PropTypes.shape({
      weight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      itemCategoryId: PropTypes.shape({
        value: PropTypes.number,
        label: PropTypes.string,
      }),
      itemContent: PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string,
        default: PropTypes.bool,
      }),
      itemCondition: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.string,
          label: PropTypes.string,
          required: PropTypes.bool,
        }),
      ),
      description: PropTypes.string,
      conditionDescription: PropTypes.string,
      incrementalPrice: PropTypes.shape({
        value: PropTypes.number,
        label: PropTypes.string,
      }),
    }),
  ),
  ticketListValue: PropTypes.arrayOf(
    PropTypes.shape({
      ticket: PropTypes.shape({
        value: PropTypes.number,
      }),
    }),
  ),
  loading: PropTypes.bool.isRequired,
  activeSalesSession: PropTypes.instanceOf(Immutable.Map).isRequired,
  dispatchGetActiveSalesSession: PropTypes.func.isRequired,
  isEdit: PropTypes.bool,
  dispatchGetCheckIfTicketExists: PropTypes.func.isRequired,
  dispatchArrayRemove: PropTypes.func.isRequired,
  dispatchPostCargoPriceBaggage: PropTypes.func.isRequired,
  mainPassengerValue: PropTypes.shape({
    value: PropTypes.number,
    label: PropTypes.string,
  }),
  specialItemListValue: PropTypes.arrayOf(
    PropTypes.shape({
      weight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  ),
  dispatchClearCargoPriceBaggage: PropTypes.func.isRequired,
  loadingCargoPriceBaggage: PropTypes.bool,

  dispatchGetCreditNotes: PropTypes.func.isRequired,
  businessIdValue: PropTypes.shape({
    value: PropTypes.number,
    label: PropTypes.string,
  }),
  dispatchClearCreditNotes: PropTypes.func.isRequired,
  specialIncrementalPriceValue: PropTypes.shape({
    value: PropTypes.number,
    label: PropTypes.string,
  }),
  baggageDiscountCodeValue: PropTypes.shape({
    id: PropTypes.number,
    discountType: PropTypes.string,
    discount: PropTypes.number,
    maxAmount: PropTypes.number,
  }),
  itemConditionSpecialOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
      isDefault: PropTypes.bool,
    }),
  ),
  itemConditionRegularOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
      isDefault: PropTypes.bool,
    }),
  ),
  dispatchGetItemConditions: PropTypes.func.isRequired,
  pinpadLocked: PropTypes.bool.isRequired,
  hasPinpadPayment: PropTypes.bool.isRequired,
  excessTransactionListValue: PropTypes.arrayOf(
    PropTypes.shape({
      paymentMethod: PropTypes.shape({
        value: PropTypes.number,
        label: PropTypes.string,
      }),
      amount: PropTypes.number,
    }),
  ),
};

BaggageForm.defaultProps = {
  initialValues: {},

  baggageItemListValue: [],
  ticketListValue: [],
  isEdit: false,
  mainPassengerValue: null,
  specialItemListValue: [],
  loadingCargoPriceBaggage: false,

  businessIdValue: undefined,
  specialIncrementalPriceValue: undefined,
  baggageDiscountCodeValue: undefined,
  itemConditionSpecialOptions: [],
  itemConditionRegularOptions: [],
  excessTransactionListValue: [],
};

const mapStateToProps = (state) => ({
  baggageConfiguration: state.BaggageUnit.BaggageConfiguration.getIn([
    'current',
    'content',
  ]),
  baggageConfigurationActivity: !state.BaggageUnit.BaggageConfiguration.getIn([
    'current',
    'activity',
  ]).isEmpty(),
  baggageItemListValue: selector(state, 'baggageItemList'),
  ticketListValue: selector(state, 'ticketList'),
  loading: !state.BaggageUnit.Baggage.getIn(['current', 'activity']).isEmpty(),
  activeSalesSession: state.SalesUnit.SalesSession.getIn(['active', 'content']),
  mainPassengerValue: selector(state, 'mainPassenger'),
  specialItemListValue: selector(state, 'specialItemList'),
  loadingCargoPriceBaggage: !state.CargoUnit.Cargo.getIn([
    'baggagePrice',
    'activity',
  ]).isEmpty(),

  businessIdValue: selector(state, 'businessId'),

  specialIncrementalPriceValue: selector(state, 'specialIncrementalPrice'),

  baggageDiscountCodeValue: selector(state, 'baggageDiscountCode'),

  itemConditionSpecialOptions: state.BaggageUnit.ItemCondition.getIn([
    'special',
    'content',
    'content',
  ]),

  itemConditionRegularOptions: state.BaggageUnit.ItemCondition.getIn([
    'regular',
    'content',
    'content',
  ]),
  excessTransactionListValue: selector(state, 'excessTransactionList'),
  pinpadLocked: isPinpadLocked('BaggageForm')(state),
  hasPinpadPayment: (selector(state, 'excessTransactionList') || []).some(
    (transaction) => transaction && transaction.pinpadPaid,
  ),
});

const mapDispatchToProps = {
  dispatchChange: change,
  dispatchGetBaggageConfiguration: getBaggageConfiguration,
  dispatchClearBaggageConfiguration: clearBaggageConfiguration,
  dispatchGetActiveSalesSession: getActiveSalesSession,
  dispatchGetCheckIfTicketExists: getCheckIfTicketExists,
  dispatchArrayRemove: arrayRemove,
  dispatchGetItemConditions: getItemConditions,
  dispatchPostCargoPriceBaggage: postCargoPriceBaggage,
  dispatchClearCargoPriceBaggage: clearCargoPriceBaggage,
  dispatchGetCreditNotes: getCreditNotes,
  dispatchClearCreditNotes: clearCreditNotes,
};

export default reduxForm({
  form: 'BaggageForm',
  initialValues: {
    contingencyAssignmentDocumentDate: tzNormalizeDate().slice(0, 16),
  },
})(connect(mapStateToProps, mapDispatchToProps)(BaggageForm));
